// Parametric retractable lateral-arm awning.
// Builds a credible model from a tier + width + projection + fabric pitch.
// All shapes are built per-rebuild and disposed cleanly (mirrors PergolaBuilder).

import * as THREE from 'three';
import { computeFurnitureLayout } from './FurnitureLayout.js';

const FT_TO_M = 0.3048;
const IN_TO_M = 0.0254;

// Width "key" → feet (matches wizard widthKey S/M/L).
const WIDTH_FT = { S: 11, M: 15, L: 20 };
// Per-tier maximum projection in meters. Real Azenco retractable awnings
// scale projection with width — wider tiers offer deeper coverage.
const PROJECTION_M = { S: 2.5, M: 3.0, L: 3.5 };

// Fabric pitch is mechanically constrained on real awnings — they can't go
// flat or tilt up. Anything outside this range is clamped at the setter.
const PITCH_MIN_DEG = 5;
const PITCH_MAX_DEG = 15;

// Fixed house dimensions — generous architectural backdrop that always
// extends well past any awning size. Patio center sits at the world origin
// so the worldGroup rotates around the patio as a turntable; the wall sits
// at the patio's back edge.
const HOUSE_WALL_W   = 9.144;   // 30 ft wide — extends past the largest awning by ~5 ft each side
const HOUSE_WALL_H   = 2.74;    // 9 ft tall — top of wall is the awning mount line
const HOUSE_WALL_T   = 0.18;    // 7 in
const HOUSE_DOOR_W   = 2.44;    // 96 in (8 ft modern slider)
const HOUSE_DOOR_H   = 2.03;    // 80 in
const HOUSE_PATIO_W  = 9.144;   // 30 ft wide
const HOUSE_PATIO_D  = 5.486;   // 18 ft deep — well past the deepest awning + furniture (~4.4 m of usage)
const WALL_FRONT_Z   = -HOUSE_PATIO_D / 2;   // wall at -2.743 m; patio centers on origin
const CASSETTE_GAP   = 0.001;   // 1 mm gap between wall front and cassette back

export class AwningBuilder {
  constructor(scene, materials) {
    this.scene = scene;
    this.mats = materials;

    // Single source of truth for wall-mount alignment.
    this.WALL_FRONT_Z = WALL_FRONT_Z;

    // The entire house+awning assembly lives in this single group so it
    // rotates as one rigid turntable around the patio center. The sun stays
    // in real astronomical world space (added directly to scene by AwningApp);
    // rotating worldGroup with facingDeg simulates the user's house orientation.
    this.worldGroup = new THREE.Group();
    this.scene.add(this.worldGroup);

    // Logical state.
    this.tier = 'signature';
    this.widthKey = 'M';
    this.extension = 1.0;            // 0..1 (retracted..fully extended)
    this.pitchDeg = 14;              // fabric pitch from horizontal
    this.facingDeg = 270;            // 270 = wall on east, patio extends west (afternoon sun)
    this.dropScreenExtension = 0;    // 0..1 — independent vertical drop screen

    this.fabric = null;              // mesh ref for shade-coverage raycasts
    this._dropScreenMesh = null;     // mesh ref for drop screen
    this._rollerMat = null;          // lazy darker clone of cassette material

    this.build();
    this._applyFacing();      // apply default 270° rotation
  }

  setTier(tier)         { this.tier = tier; this.build(); }
  setWidthKey(key)      { this.widthKey = key; this.build(); }
  // Floor at 3% so the valance never disappears into the cassette — real
  // retractable awnings always show some valance at minimum deployment.
  setExtension(t)       { this.extension = Math.max(0.03, Math.min(1, t)); this.build(); }
  setPitchDeg(deg) {
    // Real retractable awnings can only pitch downward within a 5°–15° range.
    this.pitchDeg = Math.max(PITCH_MIN_DEG, Math.min(PITCH_MAX_DEG, deg));
    this.build();
  }

  // Drop screen — vertical retractable shade hanging from the front bar.
  // Independent of the main extension; animated via mesh.scale.y because
  // the geometry's origin is at the top edge.
  setDropScreenExtension(percent) {
    this.dropScreenExtension = Math.max(0, Math.min(1, percent));
    this._applyDropScreenExtension();
  }

  _applyDropScreenExtension() {
    if (!this._dropScreenMesh) return;
    // When the main awning is barely extended, the front bar sits at/inside
    // the cassette and a drop screen hanging from there would clip the
    // housing. Hide the screen regardless of its own slider in that case.
    const mainRetracted = this.extension < 0.05;
    if (mainRetracted || this.dropScreenExtension <= 0.001) {
      this._dropScreenMesh.visible = false;
    } else {
      this._dropScreenMesh.visible = true;
      this._dropScreenMesh.scale.y = this.dropScreenExtension;
    }
  }

  // facingDeg is a compass bearing (0=N, 90=E, 180=S, 270=W). Rotate the
  // worldGroup directly by that bearing — the entire house+awning assembly
  // turns as one turntable while the sun stays astronomically fixed.
  setFacingDeg(deg) {
    this.facingDeg = deg;
    this.worldGroup.rotation.y = THREE.MathUtils.degToRad(deg);
  }

  // ── Build & dispose ─────────────────────────────────────────────────
  build() {
    this._dispose();

    // Awning footprint — projection scales per width tier (does NOT depend on
    // extension). The fabric/front-bar/arms then use extProjM (extension-
    // scaled) for retraction, but everything else (furniture, patio,
    // brackets) sees the full projection so the scene stays stable while
    // the user drags the extension slider.
    this.widthM      = (WIDTH_FT[this.widthKey] || WIDTH_FT.M) * FT_TO_M;
    this.projectionM = PROJECTION_M[this.widthKey] || PROJECTION_M.M;
    const extProjM   = this.projectionM * this.extension;

    console.log(
      '[awning] build called with width:', this.widthM.toFixed(3),
      'projection:', this.projectionM.toFixed(3),
      '(extension-scaled:', extProjM.toFixed(3) + ')',
    );

    // Cassette dims + position — top of cassette = top of wall, back face
    // touches wall front. All downstream geometry references these.
    const cas = this._getCassetteDims();

    this._buildHouseAndPatio();
    this._buildHousing(this.widthM, cas);
    this._buildBracketsAlongWidth(this.widthM, cas);
    if (this.extension > 0.02) {
      this._buildArmsAndFabric(this.widthM, extProjM, cas, this.pitchDeg);
    }
    // Drop screen mechanically tracks the actual front bar — uses extProjM
    // so the screen retracts with the awning. Built unconditionally; the
    // visibility guard in _applyDropScreenExtension hides it when the main
    // awning is too retracted for the screen to make physical sense.
    this._buildDropScreen(this.widthM, extProjM, cas, this.pitchDeg);
    this._applyFacing();

    console.log(
      '[awning] worldGroup contents:',
      this.worldGroup.children.map(c => c.userData.part || c.type),
    );
  }

  // Tier-specific cassette dimensions and world-space position. Cassette
  // top is flush with wall top (HOUSE_WALL_H), back face touches wall front.
  _getCassetteDims() {
    let H, D;
    if (this.tier === 'essential')      { H = 0.18; D = 0.30; }
    else if (this.tier === 'heritage')  { H = 0.34; D = 0.36; }
    else /* signature */                { H = 0.26; D = 0.32; }
    const centerY = HOUSE_WALL_H - H / 2;                 // top flush with wall top
    const centerZ = WALL_FRONT_Z + CASSETTE_GAP + D / 2;  // back face 1 mm off wall
    return { H, D, centerY, centerZ };
  }

  _dispose() {
    while (this.worldGroup.children.length) {
      const c = this.worldGroup.children[0];
      this.worldGroup.remove(c);
      c.traverse(o => {
        if (o.geometry) o.geometry.dispose();
      });
    }
    this.fabric = null;
    this._dropScreenMesh = null;
  }

  _applyFacing() {
    this.worldGroup.rotation.y = THREE.MathUtils.degToRad(this.facingDeg);
  }

  // ── House wall + patio plane (the world the awning lives in) ────────
  // Generous fixed backdrop (30 × 18 ft) that always extends past any
  // awning size — wall and patio dimensions are constants regardless of
  // tier. Wall sits at the back edge of the patio.
  _buildHouseAndPatio() {
    const wallW = HOUSE_WALL_W;
    const wallH = HOUSE_WALL_H;
    const wallT = HOUSE_WALL_T;
    const doorW = HOUSE_DOOR_W;
    const doorH = HOUSE_DOOR_H;
    const patioWidth = HOUSE_PATIO_W;
    const patioDepth = HOUSE_PATIO_D;

    // Wall as an extruded shape with a rectangular hole for the door,
    // centered on X at floor level.
    const wallShape = new THREE.Shape();
    wallShape.moveTo(-wallW / 2, 0);
    wallShape.lineTo( wallW / 2, 0);
    wallShape.lineTo( wallW / 2, wallH);
    wallShape.lineTo(-wallW / 2, wallH);
    wallShape.lineTo(-wallW / 2, 0);

    const doorHole = new THREE.Path();
    doorHole.moveTo(-doorW / 2, 0);
    doorHole.lineTo( doorW / 2, 0);
    doorHole.lineTo( doorW / 2, doorH);
    doorHole.lineTo(-doorW / 2, doorH);
    doorHole.lineTo(-doorW / 2, 0);
    wallShape.holes.push(doorHole);

    const wallGeo = new THREE.ExtrudeGeometry(wallShape, {
      depth: wallT,
      bevelEnabled: false,
    });
    const wall = new THREE.Mesh(wallGeo, this.mats.wall);
    // Extrudes toward +Z; pull back so the visible front face lands at WALL_FRONT_Z.
    wall.position.set(0, 0, WALL_FRONT_Z - wallT);
    wall.castShadow = true;
    wall.receiveShadow = true;
    wall.userData.part = 'wall';
    this.worldGroup.add(wall);

    // Glass door + frame + interior, mounted inside the wall cutout.
    this._buildGlassDoor(doorW, doorH, wallT);

    // Patio extends from the wall in +Z by patioDepth.
    const patioGeo = new THREE.PlaneGeometry(patioWidth, patioDepth);
    patioGeo.setAttribute('uv2', patioGeo.attributes.uv);   // aoMap needs uv2
    const patio = new THREE.Mesh(patioGeo, this.mats.patio);
    patio.rotation.x = -Math.PI / 2;
    patio.position.set(0, 0, WALL_FRONT_Z + patioDepth / 2);
    patio.receiveShadow = true;
    patio.userData.part = 'patio';
    this.worldGroup.add(patio);
    this.patio = patio;

    // Lifestyle furniture — uses FULL projection (decoupled from extension).
    this._buildFurniture(this.widthM, this.projectionM, patioWidth, patioDepth);
  }

  _buildGlassDoor(doorW, doorH, wallThickness) {
    // Interior plane sits 1 mm forward of the wall back face — looking through
    // the glass slider from the patio, you see a flat dark plane filling the
    // door cutout, not an empty cube of space.
    const interior = new THREE.Mesh(
      new THREE.PlaneGeometry(doorW + 0.1, doorH + 0.1),
      this.mats.interior,
    );
    interior.position.set(0, doorH / 2, WALL_FRONT_Z - wallThickness + 0.001);
    interior.receiveShadow = true;
    interior.userData.part = 'door-interior';
    this.worldGroup.add(interior);

    // Frame — outer perimeter rails (top, bottom, left, right) in dark aluminum.
    const frameTh = 0.06;          // rail width
    const frameDepth = 0.08;
    const fz = WALL_FRONT_Z - wallThickness / 2;  // centered in the wall thickness

    const tag = (mesh, part) => { mesh.userData.part = part; return mesh; };

    const top = tag(new THREE.Mesh(
      new THREE.BoxGeometry(doorW, frameTh, frameDepth),
      this.mats.doorFrame,
    ), 'door-frame-top');
    top.position.set(0, doorH - frameTh / 2, fz);
    top.castShadow = true;
    this.worldGroup.add(top);

    const bottom = tag(new THREE.Mesh(
      new THREE.BoxGeometry(doorW, frameTh, frameDepth),
      this.mats.doorFrame,
    ), 'door-frame-bottom');
    bottom.position.set(0, frameTh / 2, fz);
    bottom.castShadow = true;
    this.worldGroup.add(bottom);

    const sideGeo = new THREE.BoxGeometry(frameTh, doorH, frameDepth);
    const left = tag(new THREE.Mesh(sideGeo, this.mats.doorFrame), 'door-frame-left');
    left.position.set(-doorW / 2 + frameTh / 2, doorH / 2, fz);
    this.worldGroup.add(left);
    const right = tag(new THREE.Mesh(sideGeo, this.mats.doorFrame), 'door-frame-right');
    right.position.set(doorW / 2 - frameTh / 2, doorH / 2, fz);
    this.worldGroup.add(right);

    // Center meeting stile (between the two sliding panels).
    const stile = tag(new THREE.Mesh(
      new THREE.BoxGeometry(frameTh * 0.7, doorH - 2 * frameTh, frameDepth),
      this.mats.doorFrame,
    ), 'door-stile');
    stile.position.set(0, doorH / 2, fz);
    this.worldGroup.add(stile);

    // Two glass panels (left + right sliders).
    const panelW = (doorW - 3 * frameTh) / 2;
    const panelH = doorH - 2 * frameTh;
    const panelGeo = new THREE.PlaneGeometry(panelW, panelH);
    const glassL = tag(new THREE.Mesh(panelGeo, this.mats.glass), 'door-glass-left');
    glassL.position.set(-doorW / 4 - frameTh * 0.15, doorH / 2, fz + 0.001);
    this.worldGroup.add(glassL);
    const glassR = tag(new THREE.Mesh(panelGeo, this.mats.glass), 'door-glass-right');
    glassR.position.set( doorW / 4 + frameTh * 0.15, doorH / 2, fz + 0.001);
    this.worldGroup.add(glassR);

    // Slim handles on each panel.
    const handleGeo = new THREE.BoxGeometry(0.025, 0.18, 0.025);
    const handleL = tag(new THREE.Mesh(handleGeo, this.mats.doorFrame), 'door-handle-left');
    handleL.position.set(-frameTh * 0.5, doorH / 2 - 0.15, fz + 0.05);
    this.worldGroup.add(handleL);
    const handleR = tag(new THREE.Mesh(handleGeo, this.mats.doorFrame), 'door-handle-right');
    handleR.position.set( frameTh * 0.5, doorH / 2 - 0.15, fz + 0.05);
    this.worldGroup.add(handleR);
  }

  // Build the lifestyle furniture set per the awning width tier. Layout decisions
  // (what pieces, where, how big) live in FurnitureLayout.js — this method just
  // turns each piece description into Three.js geometry. Furniture position is
  // anchored to the wall and uses FULL projection, so it never moves with the
  // extension slider.
  _buildFurniture(widthM, projectionM, patioWidth, patioDepth) {
    if (projectionM <= 0.05) return;
    const layout = computeFurnitureLayout(widthM, projectionM, WALL_FRONT_Z, patioWidth, patioDepth);
    for (const piece of layout) {
      const group = this._buildFurniturePiece(piece);
      if (!group) continue;
      group.position.set(piece.position.x, piece.position.y, piece.position.z);
      group.rotation.y = piece.rotationY;
      group.userData.part = `furniture-${piece.type}`;
      this.worldGroup.add(group);
    }
  }

  // Dispatch to a per-type sub-builder. Each returns a THREE.Group whose
  // local origin is at the FLOOR center of the piece's footprint, so the
  // caller can position it in awning-local coords without any height math.
  _buildFurniturePiece(piece) {
    switch (piece.type) {
      case 'sectional':     return this._buildSofa(piece.dimensions, true);
      case 'lounge-chair':  return this._buildSofa(piece.dimensions, false);
      case 'coffee-table':  return this._buildCoffeeTable(piece.dimensions);
      case 'side-table':    return this._buildSideTable(piece.dimensions);
      case 'dining-table':  return this._buildDiningTable(piece.dimensions);
      case 'dining-chair':  return this._buildDiningChair(piece.dimensions);
      default:              return null;
    }
  }

  // Sectionals (withPillows=true) keep their wicker base block — sofas
  // typically sit skirted to the floor. Lounge chairs (withPillows=false)
  // are restructured with visible legs so they read as chairs, not ottomans.
  _buildSofa(dims, withPillows) {
    return withPillows
      ? this._buildSofaSkirted(dims, true)
      : this._buildLoungeChair(dims);
  }

  // Wicker base + backrest + arms + cushions. Used for sectionals and loveseats.
  _buildSofaSkirted({ width: w, depth: d, height: h }, withPillows) {
    const g = new THREE.Group();
    const baseH = h * 0.51;
    const cushH = 0.13;
    const backH = h * 0.64;
    const backD = Math.min(0.16, d * 0.18);
    const armW  = Math.min(0.16, w * 0.18);

    const base = new THREE.Mesh(new THREE.BoxGeometry(w, baseH, d), this.mats.wicker);
    base.position.set(0, baseH / 2, 0);
    base.castShadow = true; base.receiveShadow = true;
    g.add(base);

    const back = new THREE.Mesh(new THREE.BoxGeometry(w, backH, backD), this.mats.wicker);
    back.position.set(0, baseH + backH / 2, -d / 2 + backD / 2);
    back.castShadow = true; back.receiveShadow = true;
    g.add(back);

    const armGeo = new THREE.BoxGeometry(armW, backH, d);
    for (const sx of [-1, 1]) {
      const arm = new THREE.Mesh(armGeo, this.mats.wicker);
      arm.position.set(sx * (w / 2 - armW / 2), baseH + backH / 2, 0);
      arm.castShadow = true; arm.receiveShadow = true;
      g.add(arm);
    }

    const usableW = w - 2 * armW - 0.04;
    const cushCount = w > 1.8 ? 3 : (w > 1.0 ? 2 : 1);
    const cushSpacing = 0.025;
    const cushW = (usableW - (cushCount - 1) * cushSpacing) / cushCount;
    const cushD = d - 0.18;
    for (let i = 0; i < cushCount; i++) {
      const cx = -usableW / 2 + cushW / 2 + i * (cushW + cushSpacing);
      const seat = new THREE.Mesh(
        new THREE.BoxGeometry(cushW, cushH, cushD),
        this.mats.cushion,
      );
      seat.position.set(cx, baseH + cushH / 2, 0.02);
      seat.castShadow = true; seat.receiveShadow = true;
      g.add(seat);

      const bcushH = 0.42;
      const bcushD = 0.22;
      const bc = new THREE.Mesh(
        new THREE.BoxGeometry(cushW - 0.02, bcushH, bcushD),
        this.mats.cushion,
      );
      bc.position.set(cx, baseH + cushH + bcushH / 2, -d / 2 + bcushD / 2 + 0.05);
      bc.castShadow = true; bc.receiveShadow = true;
      g.add(bc);
    }

    if (withPillows) {
      const pillowGeo = new THREE.BoxGeometry(0.30, 0.30, 0.10);
      const spots = cushCount === 3 ? [-w * 0.27, 0, w * 0.27]
                  : cushCount === 2 ? [-w * 0.22, w * 0.22]
                  : [0];
      for (const px of spots) {
        const p = new THREE.Mesh(pillowGeo, this.mats.pillow);
        p.position.set(px, baseH + cushH + 0.18, -d / 2 + 0.32);
        p.rotation.z = (Math.random() - 0.5) * 0.16;
        p.castShadow = true; p.receiveShadow = true;
        g.add(p);
      }
    }

    return g;
  }

  // Lounge chair with visible aluminum legs and an open frame — reads as a
  // real outdoor chair instead of a solid ottoman block.
  // Vertical stack: 18 cm legs + 12 cm seat cushion + 55 cm back ≈ 85 cm.
  _buildLoungeChair({ width: w, depth: d }) {
    const g = new THREE.Group();
    const legH  = 0.18;
    const cushH = 0.12;
    const backH = 0.55;
    const backD = Math.min(0.10, d * 0.12);

    this._addChairLegs(g, w, d, 0.025, legH, 0.04);

    // Thin frame rails connecting the legs at seat-bottom — gives the chair structure.
    const railTh = 0.025;
    const railY  = legH - railTh / 2;
    const railLong  = new THREE.BoxGeometry(w - 0.02, railTh, railTh);
    const rail1 = new THREE.Mesh(railLong, this.mats.cassette);
    rail1.position.set(0, railY, -d / 2 + 0.04);
    g.add(rail1);
    const rail2 = new THREE.Mesh(railLong, this.mats.cassette);
    rail2.position.set(0, railY,  d / 2 - 0.04);
    g.add(rail2);
    const railShort = new THREE.BoxGeometry(railTh, railTh, d - 0.10);
    const rail3 = new THREE.Mesh(railShort, this.mats.cassette);
    rail3.position.set(-w / 2 + 0.04, railY, 0);
    g.add(rail3);
    const rail4 = new THREE.Mesh(railShort, this.mats.cassette);
    rail4.position.set( w / 2 - 0.04, railY, 0);
    g.add(rail4);

    // Seat cushion — sits ON TOP of the legs.
    const cushion = new THREE.Mesh(
      new THREE.BoxGeometry(w - 0.06, cushH, d - 0.06),
      this.mats.cushion,
    );
    cushion.position.set(0, legH + cushH / 2, 0);
    cushion.castShadow = true; cushion.receiveShadow = true;
    g.add(cushion);

    // Back panel — wicker-look upholstered back rising above the seat.
    const back = new THREE.Mesh(
      new THREE.BoxGeometry(w - 0.06, backH, backD),
      this.mats.wicker,
    );
    back.position.set(0, legH + cushH + backH / 2, -d / 2 + backD / 2 + 0.02);
    back.castShadow = true; back.receiveShadow = true;
    g.add(back);

    return g;
  }

  // Add 4 cylindrical legs at the corners of a w×d footprint. Used for both
  // lounge chairs and dining chairs (different leg radius/height).
  _addChairLegs(group, w, d, legR, legH, inset) {
    const legGeo = new THREE.CylinderGeometry(legR, legR, legH, 12);
    for (const sx of [-1, 1]) {
      for (const sz of [-1, 1]) {
        const leg = new THREE.Mesh(legGeo, this.mats.cassette);
        leg.position.set(sx * (w / 2 - inset), legH / 2, sz * (d / 2 - inset));
        leg.castShadow = true; leg.receiveShadow = true;
        group.add(leg);
      }
    }
  }

  _buildCoffeeTable({ width: w, depth: d, height: h }) {
    const g = new THREE.Group();
    const slab = new THREE.Mesh(
      new THREE.BoxGeometry(w, h, d),
      this.mats.tableTop,
    );
    slab.position.set(0, h / 2, 0);
    slab.castShadow = true; slab.receiveShadow = true;
    g.add(slab);

    // Tiny wicker plinth gives the slab visual weight on the patio.
    const plinth = new THREE.Mesh(
      new THREE.BoxGeometry(Math.max(0.1, w - 0.12), 0.04, Math.max(0.1, d - 0.12)),
      this.mats.wicker,
    );
    plinth.position.set(0, 0.02, 0);
    g.add(plinth);
    return g;
  }

  _buildSideTable({ width: w, depth: d, height: h }) {
    const g = new THREE.Group();
    const slab = new THREE.Mesh(
      new THREE.BoxGeometry(w, h, d),
      this.mats.tableTop,
    );
    slab.position.set(0, h / 2, 0);
    slab.castShadow = true; slab.receiveShadow = true;
    g.add(slab);

    // Single column under the slab — woven plinth.
    const col = new THREE.Mesh(
      new THREE.BoxGeometry(w * 0.45, h * 0.92, d * 0.45),
      this.mats.wicker,
    );
    col.position.set(0, h * 0.46, 0);
    col.castShadow = true; col.receiveShadow = true;
    g.add(col);
    return g;
  }

  _buildDiningTable({ width: w, depth: d, height: h }) {
    const g = new THREE.Group();
    const tabletopH = 0.04;
    const top = new THREE.Mesh(
      new THREE.BoxGeometry(w, tabletopH, d),
      this.mats.tableTop,
    );
    top.position.set(0, h - tabletopH / 2, 0);
    top.castShadow = true; top.receiveShadow = true;
    g.add(top);

    // Four cylindrical legs (cassette material stands in for dark aluminum).
    const legR = 0.025;
    const legH = h - tabletopH;
    const legGeo = new THREE.CylinderGeometry(legR, legR, legH, 12);
    const legInsetX = w / 2 - 0.12;
    const legInsetZ = d / 2 - 0.12;
    for (const sx of [-1, 1]) {
      for (const sz of [-1, 1]) {
        const leg = new THREE.Mesh(legGeo, this.mats.cassette);
        leg.position.set(sx * legInsetX, legH / 2, sz * legInsetZ);
        leg.castShadow = true; leg.receiveShadow = true;
        g.add(leg);
      }
    }
    return g;
  }

  _buildDiningChair({ width: w, depth: d, height: h }) {
    const g = new THREE.Group();
    const legH = 0.42;
    const seatThickness = 0.06;
    const cushionH = 0.05;
    const backH = h - (legH + seatThickness + cushionH);
    const backThickness = 0.04;

    this._addChairLegs(g, w, d, 0.02, legH, 0.04);

    // Seat (wicker frame) — sits on the legs.
    const seat = new THREE.Mesh(
      new THREE.BoxGeometry(w, seatThickness, d),
      this.mats.wicker,
    );
    seat.position.set(0, legH + seatThickness / 2, 0);
    seat.castShadow = true; seat.receiveShadow = true;
    g.add(seat);

    // Cushion on top of the seat.
    const cushion = new THREE.Mesh(
      new THREE.BoxGeometry(w - 0.06, cushionH, d - 0.06),
      this.mats.cushion,
    );
    cushion.position.set(0, legH + seatThickness + cushionH / 2, 0);
    cushion.castShadow = true; cushion.receiveShadow = true;
    g.add(cushion);

    // Vertical back panel — rises from the top of the seat at the -Z side.
    const back = new THREE.Mesh(
      new THREE.BoxGeometry(w, backH, backThickness),
      this.mats.wicker,
    );
    back.position.set(0, legH + seatThickness + cushionH + backH / 2, -d / 2 + backThickness / 2);
    back.castShadow = true; back.receiveShadow = true;
    g.add(back);

    return g;
  }

  // ── Tier-specific housing ──────────────────────────────────────────
  // Cassette housing + end caps + interior roller tube. All three pieces
  // share a single tier-specific cross-section returned by _getHousingProfile,
  // so future features (animated retraction, exploded view, real CAD swap-in)
  // can build on the same underlying shape.
  // _exit = the world-space point where the fabric leaves the cassette.

  // Returns a THREE.Shape representing the cassette cross-section in
  // (depth, height) units. Origin is the cross-section's center; +X is depth
  // toward the patio, +Y is up. Used for both housing extrusion + end caps.
  _getHousingProfile(tier, H, D) {
    const w = D / 2, h = H / 2;
    const wallT = 0.04;       // top/bottom panel thickness
    const wallB = 0.03;       // back/front panel thickness
    const shape = new THREE.Shape();

    if (tier === 'heritage') {
      // Full cassette — closed rounded-rectangle (D-shape).
      const r = Math.min(0.04, h * 0.55);
      shape.moveTo(-w + r, -h);
      shape.lineTo( w - r, -h);
      shape.quadraticCurveTo( w, -h,  w, -h + r);
      shape.lineTo( w,  h - r);
      shape.quadraticCurveTo( w,  h,  w - r,  h);
      shape.lineTo(-w + r,  h);
      shape.quadraticCurveTo(-w,  h, -w,  h - r);
      shape.lineTo(-w, -h + r);
      shape.quadraticCurveTo(-w, -h, -w + r, -h);
    } else if (tier === 'essential') {
      // Open hood — solid TOP cap + BACK wall; open at front and bottom.
      shape.moveTo(-w, -h);
      shape.lineTo(-w + wallB, -h);
      shape.lineTo(-w + wallB,  h - wallT);
      shape.lineTo( w,           h - wallT);
      shape.lineTo( w,           h);
      shape.lineTo(-w,           h);
      shape.lineTo(-w,          -h);
    } else {
      // Signature partial cassette — top + back + bottom + bottom-front lip,
      // with the upper-front quadrant left open so the fabric can exit cleanly.
      const lipTop = -h + H * 0.4;
      shape.moveTo(-w, -h);
      shape.lineTo( w,           -h);
      shape.lineTo( w,            lipTop);
      shape.lineTo( w - wallB,    lipTop);
      shape.lineTo( w - wallB,   -h + wallT);
      shape.lineTo(-w + wallB,   -h + wallT);
      shape.lineTo(-w + wallB,    h - wallT);
      shape.lineTo( w,            h - wallT);
      shape.lineTo( w,            h);
      shape.lineTo(-w,            h);
      shape.lineTo(-w,           -h);
    }
    return shape;
  }

  _buildHousing(widthM, cas) {
    const tier = this.tier;
    const { H, D, centerY, centerZ } = cas;

    const backZ  = centerZ - D / 2;            // = WALL_FRONT_Z + CASSETTE_GAP
    const frontZ = centerZ + D / 2;
    const topY   = centerY + H / 2;            // = HOUSE_WALL_H
    this._exit = { y: topY, z: frontZ };

    // Interior cavity dimensions — used to size + position the roller tube.
    // Heritage and Signature have top + bottom panels (4 cm each), so cavity =
    // H − 2·wallT centered on the housing. Essential is an open hood: only the
    // top cap (4 cm) constrains the cavity, so the effective cavity drops down.
    const wallT = 0.04;
    let cavityH, cavityCenterYOffset;
    if (tier === 'essential') {
      cavityH = H - wallT;
      cavityCenterYOffset = -wallT / 2;
    } else {
      cavityH = H - 2 * wallT;
      cavityCenterYOffset = 0;
    }

    const profile = this._getHousingProfile(tier, H, D);
    const extrudeOpts = { bevelEnabled: false, curveSegments: 12 };

    // Housing — extrude the profile along world X (awning width axis).
    const housingGeo = new THREE.ExtrudeGeometry(profile, { ...extrudeOpts, depth: widthM });
    housingGeo.translate(0, 0, -widthM / 2);
    const housing = new THREE.Mesh(housingGeo, this.mats.cassette);
    housing.position.set(0, centerY, centerZ);
    housing.rotation.y = -Math.PI / 2;
    housing.castShadow = true; housing.receiveShadow = true;
    housing.userData.part = 'housing';
    this.worldGroup.add(housing);

    // End caps — simple outward-projecting rectangular blocks. Same outer
    // dimensions across all tiers (only Y position tracks the housing's
    // tier-specific centerY). Share the housing's material instance directly
    // so cap and cassette read as one continuous powder-coated assembly.
    const capWidth  = 0.05;          // 5 cm outward projection past the housing end
    const capHeight = H + 0.04;      // housing height + 2 cm lip top/bottom
    const capDepth  = D + 0.04;      // housing depth + 2 cm lip front/back
    const capGeo = new THREE.BoxGeometry(capWidth, capHeight, capDepth);

    const capL = new THREE.Mesh(capGeo, this.mats.cassette);
    capL.position.set(-(widthM / 2 + capWidth / 2), centerY, centerZ);
    capL.castShadow = true; capL.receiveShadow = true;
    capL.userData.part = 'end-cap-left';
    this.worldGroup.add(capL);

    const capR = new THREE.Mesh(capGeo, this.mats.cassette);
    capR.position.set(widthM / 2 + capWidth / 2, centerY, centerZ);
    capR.castShadow = true; capR.receiveShadow = true;
    capR.userData.part = 'end-cap-right';
    this.worldGroup.add(capR);

    // Subtle decorative groove along the Heritage front face.
    if (tier === 'heritage') {
      const groove = new THREE.Mesh(
        new THREE.BoxGeometry(widthM + 0.005, 0.012, 0.005),
        this.mats.aluminum,
      );
      groove.position.set(0, centerY, frontZ + 0.003);
      this.worldGroup.add(groove);
    }

    // Roller tube — sized to nearly fill the cavity (~1.5 cm clearance).
    this._buildRollerTube(widthM, centerY, centerZ, cavityH, cavityCenterYOffset);
  }

  // Aluminum roller tube the fabric wraps around. Sized so it nearly fills
  // the cassette cavity — the visible "fat" cylinder represents the tube
  // plus its fabric wrap, matching how real cassettes look when retracted.
  _buildRollerTube(widthM, centerY, centerZ, cavityH, cavityCenterYOffset) {
    if (!this._rollerMat) {
      this._rollerMat = this.mats.cassette.clone();
      this._rollerMat.color.multiplyScalar(0.5);
    }
    // Cassette gets its envMap async after HDRI loads; keep the clone in sync.
    this._rollerMat.envMap = this.mats.cassette.envMap;
    this._rollerMat.envMapIntensity = this.mats.cassette.envMapIntensity;
    this._rollerMat.needsUpdate = true;

    const radius = Math.max(0.02, (cavityH / 2) - 0.015);   // 1.5 cm clearance to interior walls
    const length = Math.max(0.1, widthM - 0.08);             // 4 cm clearance at each end
    const geo = new THREE.CylinderGeometry(radius, radius, length, 32);
    geo.rotateZ(Math.PI / 2);                                 // align cylinder axis with world X
    const roller = new THREE.Mesh(geo, this._rollerMat);
    roller.position.set(0, centerY + cavityCenterYOffset, centerZ);
    roller.castShadow = true; roller.receiveShadow = true;
    roller.userData.part = 'roller-tube';
    this.worldGroup.add(roller);
  }

  // ── Wall-mount brackets ────────────────────────────────────────────
  // Placeholder geometry that mimics the L-arm bracket photos Dana shared.
  // When real CAD GLB files arrive, AwningCadLoader will replace these.
  _buildBracketsAlongWidth(widthM, cas) {
    const widthFt = widthM / FT_TO_M;
    const count = widthFt > 18 ? 3 : 2;
    const inset = 0.4;
    // Bracket Y: position so the L-arm foot lands directly under the cassette
    // bottom (foot top = cassette bottom). Bracket plate at wall front.
    const armLegH = 6 * IN_TO_M;
    const bracketY = (cas.centerY - cas.H / 2) + armLegH;
    for (let i = 0; i < count; i++) {
      const t = count === 1 ? 0.5 : i / (count - 1);
      const x = -widthM / 2 + inset + (widthM - 2 * inset) * t;
      const bracket = this._buildPlaceholderBracket();
      bracket.position.set(x, bracketY, WALL_FRONT_Z);
      bracket.userData.part = `wall-bracket-${i}`;
      this.worldGroup.add(bracket);
    }
  }

  _buildPlaceholderBracket() {
    const g = new THREE.Group();

    // Wall plate (photo 1) — sits on +Z face of the wall. Extrude pushes plate
    // forward into +Z so the visible face is on the patio side.
    const plateW = 6 * IN_TO_M, plateH = 6 * IN_TO_M, plateT = 0.25 * IN_TO_M;
    const plateShape = new THREE.Shape();
    const w2 = plateW / 2, h2 = plateH / 2, n = 0.6 * IN_TO_M;
    plateShape.moveTo(-w2 + n, h2);
    plateShape.lineTo( w2 - n, h2);
    plateShape.lineTo( w2, h2 - n);
    plateShape.lineTo( w2, -h2);
    plateShape.lineTo(-w2, -h2);
    plateShape.lineTo(-w2, h2 - n);
    plateShape.lineTo(-w2 + n, h2);
    const plateGeo = new THREE.ExtrudeGeometry(plateShape, { depth: plateT, bevelEnabled: false });
    const plate = new THREE.Mesh(plateGeo, this.mats.cassette);   // color-matched to housing
    plate.castShadow = true; plate.receiveShadow = true;
    g.add(plate);

    // L-arm vertical leg + horizontal foot — slimmed so the bracket reads as
    // subtle structural hardware behind/below the cassette rather than a
    // dominant black L. Forward protrusion roughly halved (15 cm → 8 cm).
    const armDepth = 3 * IN_TO_M;       // was 6"  (slimmer profile)
    const armLegH  = 6 * IN_TO_M;       // was 8"
    const armFootD = 2 * IN_TO_M;       // was 5"  (foot just grips the cassette)
    const armT     = 0.75 * IN_TO_M;
    const legGeo = new THREE.BoxGeometry(1.5 * IN_TO_M, armLegH, armDepth);
    const leg = new THREE.Mesh(legGeo, this.mats.cassette);       // color-matched
    leg.position.set(0, -armLegH / 2 - 0.5 * IN_TO_M, plateT + armDepth / 2);
    leg.castShadow = true; leg.receiveShadow = true;
    g.add(leg);

    const footGeo = new THREE.BoxGeometry(1.5 * IN_TO_M, armT, armFootD);
    const foot = new THREE.Mesh(footGeo, this.mats.cassette);     // color-matched
    foot.position.set(0, -armLegH - armT / 2, plateT + armDepth + armFootD / 2 - 0.6 * IN_TO_M);
    foot.castShadow = true; foot.receiveShadow = true;
    g.add(foot);

    // Track receiver pocket on foot underside (kept aluminum — it's a
    // visually meaningful detail of the assembly).
    const pocketGeo = new THREE.BoxGeometry(1.0 * IN_TO_M, 0.7 * IN_TO_M, 1.0 * IN_TO_M);
    const pocket = new THREE.Mesh(pocketGeo, this.mats.aluminum);
    pocket.position.set(0, -armLegH - 0.9 * IN_TO_M, plateT + armDepth + armFootD - 0.8 * IN_TO_M);
    g.add(pocket);

    return g;
  }

  // ── Lateral arms + tensioned fabric + valance + front bar ──────────
  _buildArmsAndFabric(widthM, projM, cas, pitchDeg) {
    const pitchRad = THREE.MathUtils.degToRad(pitchDeg);
    const widthFt = widthM / FT_TO_M;
    const armCount = widthFt > 18 ? 3 : 2;

    // Fabric exits the top-front of the cassette — _exit was set by _buildHousing.
    const exitY = this._exit ? this._exit.y : cas.centerY + cas.H / 2;
    const exitZ = this._exit ? this._exit.z : cas.centerZ + cas.D / 2;

    // Front bar position — fabric + bar live in the same plane sloping down.
    // The bar is centered just beneath the fabric's leading edge so the fabric
    // appears to wrap onto its top.
    const frontZ = exitZ + projM * Math.cos(pitchRad);
    const frontY = exitY - projM * Math.sin(pitchRad);

    // Front bar — extruded rectangular profile (~4" tall × 1.5" deep), the way
    // real awning front rails look. Oriented horizontally along width, with its
    // long axis tilted to match fabric pitch so the fabric meets the bar's top.
    const barH = 0.10;
    const barD = 0.04;
    const barGroup = new THREE.Group();
    const barCore = new THREE.Mesh(
      new THREE.BoxGeometry(widthM + 0.06, barH, barD),
      this.mats.aluminum,
    );
    barCore.castShadow = true; barCore.receiveShadow = true;
    barGroup.add(barCore);
    // Subtle bullnose front edge for realism.
    const bullnose = new THREE.Mesh(
      new THREE.CylinderGeometry(barH / 2, barH / 2, widthM + 0.06, 20, 1, true),
      this.mats.aluminum,
    );
    bullnose.geometry.rotateZ(Math.PI / 2);
    bullnose.position.set(0, 0, barD / 2);
    bullnose.castShadow = true;
    barGroup.add(bullnose);
    // Tilt bar to match fabric pitch.
    barGroup.rotation.x = -pitchRad;
    // Position bar so its TOP meets the fabric leading edge.
    // Top of bar in local coords is +Y by barH/2; we want that to be at (frontY, frontZ).
    barGroup.position.set(
      0,
      frontY - (barH / 2) * Math.cos(pitchRad),
      frontZ - (barH / 2) * Math.sin(pitchRad),
    );
    barGroup.userData.part = 'front-bar';
    this.worldGroup.add(barGroup);

    // Fabric: PlaneGeometry, rotated so local -Y points toward the front bar
    // and local +Y points back toward the cassette. Width along local X.
    const fabricGeo = new THREE.PlaneGeometry(widthM, projM, 16, 12);
    fabricGeo.setAttribute('uv2', fabricGeo.attributes.uv);  // aoMap needs uv2 channel
    const fabric = new THREE.Mesh(fabricGeo, this.mats.fabric);
    fabric.rotation.x = -Math.PI / 2 + pitchRad;
    fabric.position.set(
      0,
      (exitY + frontY) / 2,
      (exitZ + frontZ) / 2,
    );
    fabric.castShadow = true; fabric.receiveShadow = true;
    fabric.userData.part = 'fabric';
    this.worldGroup.add(fabric);
    this.fabric = fabric;

    // Subtle catenary sag perpendicular to the slope plane (gives a real-fabric feel).
    const pos = fabricGeo.attributes.position;
    for (let i = 0; i < pos.count; i++) {
      const localY = pos.getY(i);
      const tY = localY / (projM / 2);
      const sag = -0.025 * (1 - tY * tY);
      pos.setZ(i, pos.getZ(i) + sag);
    }
    pos.needsUpdate = true;
    fabricGeo.computeVertexNormals();

    // Valance — short fabric strip hanging straight down from the front bar.
    const valanceH = 0.20;
    const valanceGeo = new THREE.PlaneGeometry(widthM, valanceH, 8, 1);
    const valance = new THREE.Mesh(valanceGeo, this.mats.fabric);
    valance.position.set(0, frontY - barH - valanceH / 2, frontZ + barD / 2);
    valance.castShadow = true; valance.receiveShadow = true;
    valance.userData.part = 'valance';
    this.worldGroup.add(valance);

    // Lateral arms — folded linkage from cassette underside to front bar.
    // Shoulder pivot sits well under the cassette so arms don't poke through
    // the fabric; wrist attaches to the BOTTOM-back of the front bar (as on
    // a real awning), keeping all hardware below the fabric plane.
    for (let i = 0; i < armCount; i++) {
      const t = armCount === 1 ? 0.5 : i / (armCount - 1);
      const inset = 0.3;
      const x = -widthM / 2 + inset + (widthM - 2 * inset) * t;
      const shoulder = new THREE.Vector3(x, exitY - 0.20, exitZ - 0.12);
      const wrist    = new THREE.Vector3(x, frontY - barH, frontZ - barD);
      const arm = this._buildLateralArm(shoulder, wrist);
      arm.userData.part = `lateral-arm-${i}`;
      this.worldGroup.add(arm);
    }
  }

  // Vertical retractable screen mounted to the actual front bar — moves
  // with the awning as it extends/retracts (standard residential drop-screen
  // wiring). Geometry origin is at the TOP edge so scale.y animates the drop
  // from 0 (retracted into bar) to 1 (full 1.22 m / 4 ft).
  //
  // Rebuilds on every build() so the anchor follows the current front bar
  // position. When the main awning is too retracted (< 5%), the visibility
  // guard in _applyDropScreenExtension hides the screen entirely.
  _buildDropScreen(widthM, extProjM, cas, pitchDeg) {
    const DROP_MAX_H = 1.22;        // 4 ft full drop
    const pitchRad = THREE.MathUtils.degToRad(pitchDeg);
    const cos = Math.cos(pitchRad);
    const sin = Math.sin(pitchRad);

    // Cassette fabric exit (top-front of cassette).
    const exitY = cas.centerY + cas.H / 2;        // = HOUSE_WALL_H
    const exitZ = cas.centerZ + cas.D / 2;

    // Front bar position at CURRENT extension — drop screen tracks the bar.
    const frontY = exitY - extProjM * sin;
    const frontZ = exitZ + extProjM * cos;

    // Bar's bottom-front edge after its -pitchRad tilt around X. Same offset
    // math as the actual front bar; lifted 5 mm below per spec so the screen
    // doesn't z-fight the bar bottom when they're coincident.
    const barH = 0.10, barD = 0.04;
    const topY = frontY - barH * cos + (barD / 2) * sin - 0.005;
    const topZ = frontZ + (barD / 2) * cos;

    const geo = new THREE.PlaneGeometry(widthM, DROP_MAX_H);
    geo.translate(0, -DROP_MAX_H / 2, 0);          // origin at TOP edge

    const mesh = new THREE.Mesh(geo, this.mats.dropScreen);
    mesh.position.set(0, topY, topZ);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.userData.part = 'drop-screen';
    this.worldGroup.add(mesh);
    this._dropScreenMesh = mesh;

    // Apply current extension state (preserved across rebuilds).
    this._applyDropScreenExtension();
  }

  _buildLateralArm(p0, p1) {
    const g = new THREE.Group();
    // Two-segment articulated arm: shoulder near wall, elbow at ~55%, wrist at front bar.
    const elbow = p0.clone().lerp(p1, 0.55);
    // Drop the elbow slightly so the arm reads as articulated.
    elbow.y -= 0.08;

    const seg1 = this._buildArmSegment(p0, elbow);
    const seg2 = this._buildArmSegment(elbow, p1);
    g.add(seg1, seg2);

    // Joint pucks — kept small so they don't read as poking through the fabric.
    const puckGeo = new THREE.SphereGeometry(0.022, 12, 8);
    for (const p of [p0, elbow, p1]) {
      const puck = new THREE.Mesh(puckGeo, this.mats.aluminum);
      puck.position.copy(p);
      puck.castShadow = true;
      g.add(puck);
    }
    return g;
  }

  _buildArmSegment(a, b) {
    const dir = b.clone().sub(a);
    const len = dir.length();
    const geo = new THREE.BoxGeometry(0.05, 0.04, len);
    const mesh = new THREE.Mesh(geo, this.mats.aluminum);
    mesh.castShadow = true;
    mesh.position.copy(a).add(b).multiplyScalar(0.5);
    mesh.lookAt(b);
    // BoxGeometry length axis is Z; lookAt orients +Z toward b. ✓
    return mesh;
  }

  // ── Public accessors ───────────────────────────────────────────────
  getFabricMesh()     { return this.fabric; }
  getDropScreenMesh() { return this._dropScreenMesh; }
  getPatioMesh()      { return this.patio; }
  getWorldGroup()     { return this.worldGroup; }
  getWidthM()         { return (WIDTH_FT[this.widthKey] || WIDTH_FT.M) * FT_TO_M; }

  // Build the list of shadow casters that the ShadeScorer should test
  // against — the main fabric is always a caster, the drop screen only
  // when deployed (visible).
  getShadeCasters() {
    const out = [];
    if (this.fabric) out.push(this.fabric);
    if (this._dropScreenMesh && this._dropScreenMesh.visible) out.push(this._dropScreenMesh);
    return out;
  }
}
